import React from 'react'
import { applyMiddleware } from 'redux'
import { Provider } from 'react-redux'
import thunk from '@al/redux-thunk'
import { composeWithDevTools } from 'redux-devtools-extension'
import { createStore as dynamicReducerCreateStore } from 'redux-dynamic-reducer'
import { createExtraInstanceHelper } from './util'

const UPDATE_THUNK_EXTRA_ARGUMENT = Symbol(
  'wrapExApp/store/updateThunkExtraArgument'
)
const STORE_SCOPE = Symbol('wrapExApp/store/scope')

export default function withRedux(App) {
  const storeHelper = createExtraInstanceHelper(withRedux, appendCtx)
  return class extends React.PureComponent {
    static displayName = `fwAppWithRedux(${
      App.displayName || App.name || 'App'
    })`

    static async getInitialProps(appCtx) {
      const injectedInitialProps = await storeHelper.handleOnGetInitialProps(
        appCtx,
        () =>
          makeStore(
            appCtx.ctx.__AL_EX_internalScope.globalConfig.redux.reducer,
            undefined
          )
      )

      // 完成 next 的 getInitialProps 生命周期
      let initialProps = {}
      // HACK 直接使用 ExtendedApp 执行会有报错，不确定原因，直接使用 App 绕过
      if ('getInitialProps' in App) {
        initialProps = await App.getInitialProps(appCtx)
      }

      // 注入后初始状态的后获取函数，在 server/render 使用
      // 由于引入了异步 reducer，在 page 默认注入 state，此刻的值可能不对
      appCtx.ctx.__AL_EX_internalScope.collectExtraInitialState(() =>
        appCtx.ctx.store.getState()
      )

      return { ...initialProps, ...injectedInitialProps }
    }

    // 无论 server、browser 均执行
    // getInitialProps 结果将作为 props 传入
    // 但 browser 初始化 ssr 渲染页面时，仅包含可序列化状态，缺少各复杂对象，需手动重建
    constructor(props, ...rest) {
      super(props, ...rest)
      storeHelper.handleOnConstructor(props, () =>
        makeStore(
          props.ctx.__AL_EX_internalScope.globalConfig.redux.reducer,
          props.initialState
        )
      )
    }

    render() {
      storeHelper.handleAppendOnRender(this.props)
      return (
        <Provider store={this.props.ctx.store}>
          <App {...this.props} />
        </Provider>
      )
    }
  }

  // 创建 store
  function makeStore(initialReducer, initialState) {
    const thunkMiddleware = thunk.withExtraArgument()
    // 注意，不确定原因，browser 基于 server initialState 初始化 store 时，会提示错误。不影响功能，忽略
    // Unexpected key "runtime" found in previous state received by the reducer.
    // Expected to find one of the known reducer keys instead: "sub-page-1". Unexpected keys will be ignored.
    const store = dynamicReducerCreateStore(
      initialReducer,
      initialState || {},
      composeWithDevTools(applyMiddleware(thunkMiddleware))
    )
    // 注入更新方法
    store[UPDATE_THUNK_EXTRA_ARGUMENT] =
      thunkMiddleware.__AL_EX_updateExtraArgument
    // 注入全局 scope
    store[STORE_SCOPE] = {}
    return store
  }

  function appendCtx(ctx, store) {
    // 挂载 store
    ctx.store = store
    // 尽可能频繁的更新 thunk extraArgument
    ctx.store[UPDATE_THUNK_EXTRA_ARGUMENT](ctx)
    // 挂载 scope
    ctx.scope = ctx.store[STORE_SCOPE]
    // 挂载 actions
    ctx.actions = ctx.__AL_EX_internalScope.globalConfig.redux.actions
  }
}
